import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { tradingAPI } from '@/lib/api';
import { TrendingUp, TrendingDown, RefreshCw, Clock, DollarSign, BarChart3, Activity } from 'lucide-react';

const fmt = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function PositionsPage() {
  const [positions, setPositions] = useState([]);
  const [history, setHistory] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');
  const [closing, setClosing] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [posRes, histRes, accRes] = await Promise.all([
        tradingAPI.getPositions(),
        tradingAPI.getHistory(200),
        tradingAPI.getAccount(),
      ]);
      setPositions(posRes.data || []);
      setHistory(histRes.data || []);
      if (accRes.data?.account_number) setAccount(accRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); const i = setInterval(loadData, 5000); return () => clearInterval(i); }, [loadData]);

  const handleClose = async (tradeId) => {
    setClosing(tradeId);
    try {
      await tradingAPI.closePosition(tradeId);
      loadData();
    } catch { /* silent */ }
    setClosing(null);
  };

  const openPnl = positions.reduce((s, p) => s + (p.profit_loss || 0), 0);
  const closedPnl = history.filter(t => t.status === 'closed').reduce((s, t) => s + (t.profit_loss || 0), 0);
  const totalTrades = positions.length + history.length;
  const winRate = history.filter(t => t.status === 'closed').length > 0
    ? ((history.filter(t => t.status === 'closed' && (t.profit_loss || 0) > 0).length / history.filter(t => t.status === 'closed').length) * 100).toFixed(1)
    : '0.0';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="positions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Positions</h1>
            <p className="text-xs text-[var(--text-secondary)] mt-1">Track your open positions and full trading activity</p>
          </div>
          <button onClick={loadData} data-testid="positions-refresh"
            className="flex items-center gap-1.5 px-3 py-2 bg-green-500/10 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/20 border border-green-500/20">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="positions-stats">
          <StatCard icon={DollarSign} label="Balance" value={fmt(account?.balance)} color="green" testId="stat-balance" />
          <StatCard icon={Activity} label="Open Positions" value={positions.length} color="blue" testId="stat-open-count" />
          <StatCard icon={TrendingUp} label="Open P/L" value={fmt(openPnl)} color={openPnl >= 0 ? 'emerald' : 'red'} testId="stat-open-pnl" />
          <StatCard icon={BarChart3} label="Realized P/L" value={fmt(closedPnl)} color={closedPnl >= 0 ? 'emerald' : 'red'} testId="stat-closed-pnl" />
          <StatCard icon={Clock} label="Win Rate" value={`${winRate}%`} color="purple" testId="stat-win-rate" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[var(--border-subtle)]">
          {[
            { key: 'open', label: `Open Positions (${positions.length})` },
            { key: 'closed', label: `Closed Trades (${history.filter(t => t.status === 'closed').length})` },
            { key: 'all', label: `All Activity (${totalTrades})` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`positions-tab-${t.key}`}
              className={`px-4 py-2.5 text-xs font-medium transition-all border-b-2 -mb-px ${tab === t.key ? 'text-green-400 border-green-400' : 'text-[var(--text-secondary)] border-transparent hover:text-[var(--text-primary)]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Open Positions */}
        {tab === 'open' && (
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg overflow-x-auto" data-testid="open-positions-table">
            {positions.length === 0 ? (
              <div className="p-12 text-center text-sm text-[var(--text-tertiary)]">No open positions</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-[var(--text-tertiary)] uppercase border-b border-[var(--border-subtle)]">
                    <th className="text-left px-5 py-3">Symbol</th>
                    <th className="text-left px-3 py-3">Type</th>
                    <th className="text-right px-3 py-3">Volume</th>
                    <th className="text-right px-3 py-3">Entry Price</th>
                    <th className="text-right px-3 py-3">Current Price</th>
                    <th className="text-right px-3 py-3">Target</th>
                    <th className="text-right px-3 py-3">Stop Loss</th>
                    {/* <th className="text-right px-3 py-3">Trade Amount</th> */}
                    <th className="text-right px-3 py-3">Spread</th>
                    <th className="text-right px-3 py-3">P/L</th>
                    <th className="text-right px-3 py-3">Opened</th>
                    <th className="text-right px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map(p => (
                    <tr key={p.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)] transition-colors" data-testid={`pos-row-${p.id}`}>
                      <td className="px-5 py-3">
                        <span className="font-mono text-xs font-semibold text-[var(--text-primary)]">{p.symbol?.toUpperCase()}/USD</span>
                        <span className="text-[10px] text-[var(--text-tertiary)] block">{p.symbol_name}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${p.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                          {p.type === 'buy' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {p.type?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{p.volume}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{fmt(p.entry_price)}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-primary)]">{fmt(p.current_price)}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs text-emerald-400/70">{p.take_profit ? fmt(p.take_profit) : '-'}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs text-red-400/70">{p.stop_loss ? fmt(p.stop_loss) : '-'}</td>
                      {/* <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{fmt(p.trade_amount)}</td> */}
                      <td className="px-3 py-3 text-right font-mono text-xs text-amber-400">{p.spread_amount ? fmt(p.spread_amount) : '-'}</td>
                      <td className={`px-3 py-3 text-right font-mono text-xs font-semibold ${(p.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(p.profit_loss || 0) >= 0 ? '+' : ''}{fmt(p.profit_loss)}
                      </td>
                      <td className="px-3 py-3 text-right text-[10px] text-[var(--text-secondary)]">
                        {p.opened_at ? new Date(p.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => handleClose(p.id)} disabled={closing === p.id} data-testid={`close-pos-${p.id}`}
                          className="px-3 py-1.5 rounded text-[10px] font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-40">
                          {closing === p.id ? 'Closing...' : 'Close'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Closed Trades */}
        {tab === 'closed' && (
          <TradeTable trades={history.filter(t => t.status === 'closed')} testId="closed-trades-table" />
        )}

        {/* All Activity */}
        {tab === 'all' && (
          <TradeTable trades={[...positions, ...history]} testId="all-trades-table" />
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  const colors = {
    green: 'bg-green-500/10 text-green-400 border-green-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };
  return (
    <div className={`p-4 rounded-lg border ${colors[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</span>
      </div>
      <p className="text-lg font-bold font-mono">{value}</p>
    </div>
  );
}

function TradeTable({ trades, testId }) {
  const fmt = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
  const sorted = [...trades].sort((a, b) => new Date(b.opened_at || 0) - new Date(a.opened_at || 0));

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg overflow-x-auto" data-testid={testId}>
      {sorted.length === 0 ? (
        <div className="p-12 text-center text-sm text-[var(--text-tertiary)]">No trades found</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-[var(--text-tertiary)] uppercase border-b border-[var(--border-subtle)]">
              <th className="text-left px-5 py-3">Symbol</th>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-center px-3 py-3">Status</th>
              <th className="text-right px-3 py-3">Volume</th>
              <th className="text-right px-3 py-3">Entry</th>
              <th className="text-right px-3 py-3">Close/Current</th>
              <th className="text-right px-3 py-3">Target</th>
              <th className="text-right px-3 py-3">SL</th>
              <th className="text-right px-3 py-3">Amount</th>
              <th className="text-right px-3 py-3">Spread</th>
              <th className="text-right px-3 py-3">P/L</th>
              <th className="text-right px-5 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(t => (
              <tr key={t.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)] transition-colors">
                <td className="px-5 py-3 font-mono text-xs font-semibold text-[var(--text-primary)]">{t.symbol?.toUpperCase()}</td>
                <td className="px-3 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                    {t.type === 'buy' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {t.type?.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${t.status === 'open' ? 'bg-green-500/15 text-green-400' : 'bg-zinc-500/15 text-[var(--text-tertiary)]'}`}>{t.status}</span>
                </td>
                <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{t.volume}</td>
                <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{fmt(t.entry_price)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{fmt(t.close_price || t.current_price)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs text-emerald-400/70">{t.take_profit ? fmt(t.take_profit) : '-'}</td>
                <td className="px-3 py-3 text-right font-mono text-xs text-red-400/70">{t.stop_loss ? fmt(t.stop_loss) : '-'}</td>
                <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{fmt(t.trade_amount)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs text-amber-400">{t.spread_amount ? fmt(t.spread_amount) : '-'}</td>
                <td className={`px-3 py-3 text-right font-mono text-xs font-semibold ${(t.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(t.profit_loss || 0) >= 0 ? '+' : ''}{fmt(t.profit_loss)}
                </td>
                <td className="px-5 py-3 text-right text-[10px] text-[var(--text-secondary)]">
                  {t.opened_at ? new Date(t.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
